/**
 * useLazyProductSearch
 *
 * Offset-based "infinite scroll" wrapper around `useProductSearch` from `@salesforce/commerce-sdk-react`.
 *
 * Why this exists (KINDW-99 / FSD PLP-03):
 *   The Range Detail PLP needs to fetch products in fixed-size batches (default 16) and append
 *   them as the user scrolls — without URL-driven pagination. We can't reuse the page-context
 *   `productSearchResult` because the page-level fetch uses a different `limit` (driven by URL
 *   `searchParams.limit`). This hook owns the entire pagination lifecycle for a given category.
 *
 * Behaviour:
 *   - Fetches page 0 immediately (offset=0, limit=pageSize).
 *   - Exposes `loadMore()` to fetch the next batch; idempotent while a fetch is in flight.
 *   - Resets when `categoryId` (or `extraRefine` content) changes — KINDW-103 chips toggle filters
 *     by mutating `extraRefine`; the grid must rewind to offset 0 and clear stale hits.
 *   - Keeps `previousData` between fetches so `hits` never flickers to empty.
 *   - Exposes `refinements` from the latest response so callers (KINDW-103 filter tags) can render
 *     facet UI without firing a second request.
 *
 * @param {object} args
 * @param {string} [args.categoryId]      — SFCC category id (`cgid`). Category mode (Range PLP).
 * @param {string} [args.searchPhrase]    — Global search phrase (`q`). Search mode (`/search`).
 *                                          Mutually exclusive with `categoryId` in practice.
 * @param {string} [args.sort]             — Sort id from URL; used only with `searchPhrase`.
 * @param {string[]} [args.urlRefine]     — `refine` string array (`_refine` from `useSearchParams`);
 *                                          used only with `searchPhrase`.
 * @param {number} [args.pageSize=16]     — Items per batch.
 * @param {string[]} [args.expand]        — optional productSearch expand override.
 * @param {string[]} [args.extraRefine]   — Additional `refine` entries (e.g. `['c_seaterCount=3|4']`)
 *                                          merged with the `cgid` refinement. KINDW-103: filter chips.
 *
 * @returns {{
 *   hits: object[],                      — accumulated hits across all loaded pages
 *   total: number,                       — total products in the (filtered) category
 *   refinements: object[],               — refinements for the current filter set (KINDW-103)
 *   hasMore: boolean,                    — true when more products are available
 *   isInitialLoading: boolean,           — true only during the very first fetch
 *   isRefining: boolean,                 — true while a refine-change refetch is in flight (KINDW-103)
 *   isLoadingMore: boolean,              — true while fetching a non-initial page
 *   error: unknown,                      — error from the most recent fetch (if any)
 *   loadMore: () => void,                — request the next batch (no-op if already loading)
 *   pageResult: object|undefined,       — latest SCAPI page payload (metadata + last batch)
 * }}
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useProductSearch} from '@salesforce/commerce-sdk-react';

import {RANGE_PLP_PAGE_SIZE} from '@amblique/king-living/app/constants';
import {LAZY_PLP_PRODUCT_SEARCH_PARAMS} from '@amblique/king-living/app/utils/api-utils';

const EMPTY_REFINE = Object.freeze([]);

export const useLazyProductSearch = ({
  categoryId,
  searchPhrase,
  sort,
  urlRefine = EMPTY_REFINE,
  pageSize = RANGE_PLP_PAGE_SIZE,
  expand,
  extraRefine = EMPTY_REFINE,
} = {}) => {
  const [offset, setOffset] = useState(0);
  const [accumulatedHits, setAccumulatedHits] = useState([]);
  const lastSeenOffsetRef = useRef(-1);

  const isSearchMode = Boolean(searchPhrase);

  const normalizedUrlRefine = useMemo(() => {
    const arr = urlRefine ?? [];
    return arr.filter((r) => typeof r === 'string' && r.length);
  }, [urlRefine]);

  const urlRefineSignature = useMemo(
    () => Array.from(new Set(normalizedUrlRefine)).sort().join('|'),
    [normalizedUrlRefine]
  );

  // Stable string signature for `extraRefine` so reference identity changes (e.g. fresh array on
  // every render) don't trigger spurious resets. We dedupe + sort so `['a', 'b']` and `['b', 'a']`
  // are treated as the same filter set.
  const extraRefineSignature = useMemo(
    () => Array.from(new Set(extraRefine ?? [])).sort().join('|'),
    [extraRefine]
  );

  // Reset when the category or filter set changes — without this, switching categories or toggling
  // a filter chip would append the new result onto the previous list.
  useEffect(() => {
    setOffset(0);
    setAccumulatedHits([]);
    lastSeenOffsetRef.current = -1;
  }, [categoryId, pageSize, extraRefineSignature, searchPhrase, sort, urlRefineSignature]);

  const refineParam = useMemo(() => {
    if (!categoryId) return [];
    const out = [`cgid=${categoryId}`];
    for (const r of extraRefine ?? []) {
      if (typeof r === 'string' && r.length) out.push(r);
    }
    return out;
  }, [categoryId, extraRefine]);

  const parameters = useMemo(() => {
    const base = {
      ...LAZY_PLP_PRODUCT_SEARCH_PARAMS,
      offset,
      limit: pageSize,
      ...(expand?.length ? {expand} : {}),
    };

    if (isSearchMode) {
      return {
        ...base,
        q: searchPhrase,
        sort: sort ?? 'best-matches',
        refine: normalizedUrlRefine,
      };
    }

    return {
      ...base,
      refine: refineParam,
    };
  }, [
    offset,
    pageSize,
    expand,
    isSearchMode,
    searchPhrase,
    sort,
    normalizedUrlRefine,
    refineParam,
  ]);

  const enabled = Boolean(categoryId) || isSearchMode;

  const {
    data: pageResult,
    isLoading,
    isFetching,
    error,
  } = useProductSearch(
    {parameters},
    {enabled, keepPreviousData: true}
  );

  // Append hits when a new page lands — guarded against double-applying the same offset (which
  // can happen on remount or on cached responses).
  useEffect(() => {
    if (!pageResult?.hits) return;
    if (pageResult.offset !== offset) return;
    if (lastSeenOffsetRef.current === offset) return;

    lastSeenOffsetRef.current = offset;

    setAccumulatedHits((prev) => {
      // First page: replace; subsequent pages: append (and de-dupe just in case).
      if (offset === 0) return pageResult.hits;

      const existingIds = new Set(prev.map((h) => h.productId));
      const newHits = pageResult.hits.filter((h) => !existingIds.has(h.productId));

      return prev.concat(newHits);
    });
  }, [pageResult, offset]);

  const total = pageResult?.total ?? 0;
  const refinements = pageResult?.refinements ?? EMPTY_REFINE;
  const hasMore = accumulatedHits.length > 0 && accumulatedHits.length < total;

  const loadMore = useCallback(() => {
    // Guard: avoid bumping offset while a fetch is already in flight or there's nothing more.
    if (isFetching) return;
    if (!hasMore) return;
    if (accumulatedHits.length === 0) return;
    setOffset(accumulatedHits.length);
  }, [isFetching, hasMore, accumulatedHits.length]);

  const isInitialLoading = isLoading && offset === 0 && accumulatedHits.length === 0;
  const isLoadingMore = isFetching && offset > 0;
  // KINDW-103: when the user toggles a filter chip we reset offset → 0 and clear accumulated hits;
  // while the new request is in flight `isLoading` stays false (cached query data → no remount),
  // so we surface a separate flag callers can use to keep skeleton/loading UI visible.
  const isRefining = isFetching && offset === 0 && accumulatedHits.length === 0 && !isInitialLoading;

  return {
    hits: accumulatedHits,
    total,
    refinements,
    hasMore,
    isInitialLoading,
    isRefining,
    isLoadingMore,
    error,
    loadMore,
    pageResult,
  };
};

export default useLazyProductSearch;
